import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';

export interface StarPickerAtomProps {
  value: number;
  onChange: (stars: number) => void;
  maxStars?: number;
  size?: 'small' | 'normal' | 'large';
  disabled?: boolean;
  className?: string;
}

const StarPickerAtom: React.FC<StarPickerAtomProps> = ({
  value,
  onChange,
  maxStars = 5,
  size = 'normal',
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  const displayValue = hoveredStar ?? value;

  const handleClick = useCallback(
    (star: number) => {
      if (!disabled) {
        onChange(star);
      }
    },
    [disabled, onChange],
  );

  const handleMouseEnter = useCallback(
    (star: number) => {
      if (!disabled) {
        setHoveredStar(star);
      }
    },
    [disabled],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredStar(null);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, star: number) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(star);
      }
    },
    [disabled, onChange],
  );

  const classes = [
    'marketplace-star-picker',
    `marketplace-star-picker--${size}`,
    disabled ? 'marketplace-star-picker--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="radiogroup"
      aria-label={t('marketplace.ratings.yourRating')}
      onMouseLeave={handleMouseLeave}
    >
      {Array.from({ length: maxStars }, (_, i) => {
        const star = i + 1;
        const isFilled = star <= displayValue;
        const isSelected = star === value;

        return (
          <button
            key={star}
            type="button"
            className={[
              'marketplace-star-picker__star',
              isFilled ? 'marketplace-star-picker__star--filled' : 'marketplace-star-picker__star--empty',
              isSelected ? 'marketplace-star-picker__star--selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="radio"
            aria-checked={isSelected}
            aria-label={t('marketplace.ratings.starsLabel', { count: star })}
            disabled={disabled}
            onClick={() => handleClick(star)}
            onMouseEnter={() => handleMouseEnter(star)}
            onKeyDown={(e) => handleKeyDown(e, star)}
          >
            {isFilled ? '\u2605' : '\u2606'}
          </button>
        );
      })}
    </div>
  );
};

export default StarPickerAtom;
